import {
	reactive,
	ref,
	Ref,
} from '@vue/composition-api';
import {
	NoteMeta,
	NoteMessage,
} from '@front-app/services/NoteApi/NoteModels';
import {
	getNotePage,
	getNoteFirstPage,
	markNoteMessageRead,
	CreateNotePayload,
	createNote,
	addParticipantsToNote,
	getNoteMetadatas,
	leaveNote,
} from '@front-app/services/NoteApi/NoteApi';
import { PartialUser, NoteUser } from '@front-app/services/UserApi/UserModel';
import { STATUS } from '@front-app/constants/state';
import { getDeeplinkPageParams } from '@front-app/components/notes/util/deeplink';
import { UserStore } from './user';

export interface MarkMessageReadPayload {
	noteId: string;
	noteMessageId: string;
}

export interface NoteStore {
	reply(newMessage: NoteMessage): Promise<void>;
	create(payload: CreateNotePayload): Promise<NoteState>;
	leave(noteId: string): Promise<void>;
	remove(noteId: string): void;
	markMessageRead(payload: MarkMessageReadPayload): Promise<void>;
	addParticipants(noteId: string, userIds: string[]): Promise<void>;
	fetch(noteId: string): Promise<void>;
	fetchDeeplink(noteId: string, discussionMessageId: string): Promise<void>;
	reset(noteId: string): Promise<void>;
	get: (noteId: string) => NoteState;
	fetchMetadata(discussionMessageId: string): Promise<void>;
	isLoadingMetadata(discussionMessageId: string): boolean;
	getMetadata(discussionMessageId: string): NoteMeta[];
	getNoteMetadata(noteId: string): NoteMeta;
}

const getEmptyNote = (): NoteState => ({
	isLoading: true,
	noteId: '',
	messages: [],
	metadata: {
		noteId: '',
		discussionMessageId: '',
		groupId: '',
		creatorId: '',
		participants: [],
		numMessages: 0,
		numUnreadMessagesSinceJoin: 0,
		subject: '',
	},
});

export interface NoteState {
	noteId: string;
	metadata: NoteMeta;
	messages: NoteMessage[];
	isLoading: boolean;
}

interface MetadataLoadingState {
	isLoading: boolean;
	hasFetched: boolean;
}

type NoteMetadataRecord = Record<string, NoteMeta>;

export const getNoteStore = (
	userStore: UserStore,
): NoteStore => {
	const notes = {} as Record<string, NoteState>;
	const metadataByNoteId = {} as NoteMetadataRecord;
	const notesByDiscussionMessageId: Record<string, Ref<string[]>> = {};
	const getNoteIdsForDiscussionMessage = (discussionMessageId: string) => {
		const current = notesByDiscussionMessageId[discussionMessageId];
		if (current) {
			return current;
		}
		notesByDiscussionMessageId[discussionMessageId] = ref([]);
		return notesByDiscussionMessageId[discussionMessageId];
	};

	const fetch = (noteId: string): Promise<void> => {
		if (notes[noteId]) {
			return Promise.resolve();
		}
		const note = reactive(getEmptyNote());
		note.isLoading = true;
		notes[noteId] = note;
		return getNoteFirstPage(noteId)
			.then((resp) => {
				Object.assign(note, resp);
				userStore.setPartials(resp.usersData.map(noteUserToUser));
			})
			.then(() => {
				note.isLoading = false;
			})
			.catch(() => {
				note.isLoading = false;
				delete notes[noteId];
			});
	};

	const reset = (noteId: string) => {
		delete notes[noteId];
		return fetch(noteId);
	};

	const leave = async (noteId: string): Promise<void> => {
		await leaveNote(noteId);
	};

	const remove = async (noteId: string): Promise<void> => {
		const current = notes[noteId];
		if (current) {
			const orderedNoteIds = getNoteIdsForDiscussionMessage(current.metadata.discussionMessageId);
			orderedNoteIds.value = orderedNoteIds.value
				.filter((id) => id !== noteId);
		}
		delete notes[noteId];
		delete metadataByNoteId[noteId];
	};

	const fetchDeeplink = (noteId: string, noteMessageId: string) => {
		if (notes[noteId]) {
			return Promise.resolve();
		}
		const note = reactive(getEmptyNote());
		note.isLoading = true;
		notes[noteId] = note;
		return getNotePage(noteId, getDeeplinkPageParams(noteMessageId))
			.then((resp) => {
				Object.assign(note, resp);
				userStore.setPartials(resp.usersData.map(noteUserToUser));
			})
			.then(() => {
				note.isLoading = false;
			})
			.catch((e) => {
				// eslint-disable-next-line no-console
				console.error('Failed to fetch Note deep link', e);
				note.isLoading = false;
				delete notes[noteId];
				return Promise.reject();
			});
	};

	const markMessageRead = async (payload: MarkMessageReadPayload): Promise<void> => {
		const { noteId, noteMessageId } = payload;
		const note = notes[noteId];
		if (!note) {
			return;
		}
		const message = note.messages.find((_) => _.noteMessageId === noteMessageId);
		if (!message) {
			return;
		}
		try {
			await markNoteMessageRead(noteId, noteMessageId);
			const newNumMessages = Math.max(note.metadata.numUnreadMessagesSinceJoin - 1, 0);
			note.metadata.numUnreadMessagesSinceJoin = newNumMessages;
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error('Error marking note message read', payload, e);
		}
	};

	const create = async (payload: CreateNotePayload): Promise<NoteState> => {
		const newNote = await createNote(payload);
		userStore.setPartials(newNote.usersData.map(noteUserToUser));
		const note = Object.assign(reactive(getEmptyNote()), newNote);
		note.isLoading = false;
		notes[note.noteId] = note;

		const orderedNoteIds = getNoteIdsForDiscussionMessage(payload.discussionMessageId);
		if (!orderedNoteIds.value.includes(newNote.noteId)) {
			orderedNoteIds.value = [newNote.noteId, ...orderedNoteIds.value];
		}

		return note;
	};

	const reply = async (newMessage: NoteMessage): Promise<void> => {
		const note = notes[newMessage.noteId];
		const metadata = metadataByNoteId[newMessage.noteId];
		if (!note) {
			return;
		}
		metadata.numMessages += 1;
		note.metadata.numMessages += 1;
		note.messages.push(newMessage);
	};

	const addParticipants = async (noteId: string, userIds: string[]): Promise<void> => {
		const note = notes[noteId];
		if (!note) {
			return;
		}
		const filteredUserIds = userIds.filter((id) => !note.metadata.participants.includes(id));
		await addParticipantsToNote(noteId, { to: filteredUserIds });
		await userStore.fetchMany(filteredUserIds);
		// eslint-disable-next-line
		note.metadata.participants = note.metadata.participants.concat(filteredUserIds);
	};

	const get = (noteId: string) => notes[noteId];

	const noteIdToMetadata = (noteId: string) => {
		const note = notes[noteId];
		if (note && !note.isLoading) {
			return note.metadata;
		}
		return metadataByNoteId[noteId];
	};

	const metadataLoadingState: Record<string, MetadataLoadingState> = {};
	const getMetadataLoadingState = (discussionMessageId: string) => {
		const current = metadataLoadingState[discussionMessageId];
		if (current) {
			return current;
		}
		metadataLoadingState[discussionMessageId] = reactive({
			isLoading: false,
			hasFetched: false,
		});
		return metadataLoadingState[discussionMessageId];
	};
	const getMetadata = (discussionMessageId: string) => getNoteIdsForDiscussionMessage(discussionMessageId)
		.value.map(noteIdToMetadata);

	const isLoadingMetadata = (discussionMessageId: string) => getMetadataLoadingState(discussionMessageId).isLoading;
	const fetchMetadata = async (discussionMessageId: string) => {
		const state = getMetadataLoadingState(discussionMessageId);
		if (state.isLoading || state.hasFetched) {
			return;
		}
		state.isLoading = true;
		const newMetadata = await getNoteMetadatas(discussionMessageId);
		newMetadata.forEach((curr) => {
			metadataByNoteId[curr.noteId] = reactive(curr);
		});
		const orderedNoteIds = getNoteIdsForDiscussionMessage(discussionMessageId);
		orderedNoteIds.value = newMetadata
			.filter(({ noteId }) => !orderedNoteIds.value.includes(noteId))
			.map((s: NoteMeta) => s.noteId)
			.concat(orderedNoteIds.value);
		state.hasFetched = true;
		state.isLoading = false;
	};

	return {
		create,
		leave,
		remove,
		reply,
		markMessageRead,
		addParticipants,
		fetch,
		fetchDeeplink,
		reset,
		get,
		fetchMetadata,
		isLoadingMetadata,
		getMetadata,
		getNoteMetadata: noteIdToMetadata,
	};
};

function noteUserToUser(user: NoteUser): PartialUser {
	return {
		status: STATUS.SUCCESS,
		id: user.userId,
		salutation: user.salutation,
		displayName: user.displayName,
		firstName: '',
		middleName: '',
		lastName: '',
		authenticated: user.authenticated,
		verified: user.verified,
	};
}
